import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { createAdApi, getAdImageApi, updateAdApi, type CampaignAd, type SuggestedAd } from "../campanhasApi";
import AdPreviewMock from "./AdPreviewMock";

const MAX_IMAGE_MB = 8;

type Props = {
  campaignId: string;
  /** Anúncio a editar; ausente = novo. */
  ad: CampaignAd | null;
  /** Campanha publicada: o anúncio alterado volta à revisão da Meta. */
  live: boolean;
  /** Textos sugeridos pela IA para esta campanha: um clique preenche o formulário. */
  suggestedAds?: SuggestedAd[];
  onClose: () => void;
  onSaved: () => void;
};

/** Imagem (JPG ou PNG, 1080×1080 recomendado) e os três textos do anúncio. */
const AdFormModal: React.FC<Props> = ({ campaignId, ad, live, suggestedAds = [], onClose, onSaved }) => {
  const [primaryText, setPrimaryText] = useState(ad?.primaryText ?? "");
  const [headline, setHeadline] = useState(ad?.headline ?? "");
  const [description, setDescription] = useState(ad?.description ?? "");
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Imagem já gravada (edição), para a prévia enquanto não se escolhe outra.
  const [savedImage, setSavedImage] = useState<string | null>(null);

  useEffect(() => {
    if (!ad) return;
    let url: string | null = null;
    getAdImageApi(campaignId, ad.id)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        setSavedImage(url);
      })
      .catch(() => setSavedImage(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [campaignId, ad]);

  useEffect(() => {
    if (!image) return setPreview(null);
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const pickImage = (file: File | undefined) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) return toast.error("A imagem precisa ser JPG ou PNG.");
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) return toast.error(`A imagem passa de ${MAX_IMAGE_MB} MB.`);
    setImage(file);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ad && !image) return toast.error("Escolha a imagem do anúncio.");
    try {
      setBusy(true);
      const payload = { primaryText: primaryText.trim(), headline: headline.trim(), description: description.trim(), image };
      if (ad) await updateAdApi(campaignId, ad.id, payload);
      else await createAdApi(campaignId, payload);
      toast.success(ad ? "Anúncio salvo." : "Anúncio incluído.");
      onSaved();
    } catch (err: any) {
      toast.error(err?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ecoDialog__overlay">
      <form className="cmpModal cmpModal--wide" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="cmp-ad-title">
        <div className="cmpModal__head">
          <h2 id="cmp-ad-title" className="cmpModal__title">
            {ad ? `Anúncio ${ad.position}` : "Novo anúncio"}
          </h2>
          <button type="button" className="ecoBtn ecoBtn--icon" aria-label="Fechar" data-modal-close onClick={onClose} disabled={busy}>
            <EcoIcon name="x" size={18} />
          </button>
        </div>

        {live ? (
          <p className="cmpNotice cmpNotice--info">
            <EcoIcon name="alertTriangle" size={16} />
            <span>A campanha já está na Meta: o anúncio alterado volta para a revisão dela antes de veicular de novo.</span>
          </p>
        ) : null}

        <div className="cmpAdForm">
        <div className="cmpAdForm__fields">
        {suggestedAds.length ? (
          <div className="aiSuggestedTexts">
            <span className="ecoLabel">Textos sugeridos pela Eco-IA</span>
            <div className="aiDrawer__starters">
              {suggestedAds.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className="aiChip"
                  title={s.primaryText}
                  onClick={() => {
                    setPrimaryText(s.primaryText);
                    setHeadline(s.headline.slice(0, 40));
                    setDescription((s.description ?? "").slice(0, 30));
                  }}
                >
                  {s.angle ? `${s.angle}: ` : ""}
                  {s.headline}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="ecoField">
          <span className="ecoLabel">Imagem{ad ? <span className="ecoLabel__optional"> (só se for trocar)</span> : null}</span>
          <div className="cmpActions" style={{ justifyContent: "flex-start", alignItems: "center" }}>
            {preview ? <img className="cmpModal__preview" src={preview} alt="Prévia da imagem escolhida" /> : null}
            <label className="ecoBtn">
              <EcoIcon name="camera" size={18} />
              {image ? "Trocar imagem" : "Escolher imagem"}
              <input
                type="file"
                accept="image/jpeg,image/png"
                className="ecoVisuallyHidden"
                onChange={(e) => pickImage(e.target.files?.[0])}
              />
            </label>
          </div>
          <p className="cmpHint">JPG ou PNG, até {MAX_IMAGE_MB} MB. Quadrada (1080×1080) serve no feed e nos stories.</p>
        </div>

        <label className="ecoField">
          <span className="ecoLabel">Texto principal</span>
          <textarea
            className="ecoInput cmpTextarea"
            rows={4}
            maxLength={500}
            required
            value={primaryText}
            onChange={(e) => setPrimaryText(e.target.value)}
            placeholder="Ex.: Procuramos entregadores freelancer na região de Niterói. Pagamento por diária, escala flexível."
          />
          <span className="cmpCount">{primaryText.length}/500</span>
        </label>

        <div className="cmpGrid">
          <label className="ecoField">
            <span className="ecoLabel">Título</span>
            <input className="ecoInput" maxLength={40} required value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Ex.: Vagas de entregador" />
            <span className="cmpCount">{headline.length}/40</span>
          </label>
          <label className="ecoField">
            <span className="ecoLabel">
              Descrição <span className="ecoLabel__optional">(opcional)</span>
            </span>
            <input className="ecoInput" maxLength={30} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex.: Inscreva-se em 2 minutos" />
            <span className="cmpCount">{description.length}/30</span>
          </label>
        </div>

        <p className="cmpHint">O botão do anúncio é sempre "Candidate-se" e leva para a página de candidatura da campanha.</p>

        </div>
        <aside className="cmpAdForm__preview" aria-label="Prévia do anúncio">
          <AdPreviewMock primaryText={primaryText} headline={headline} description={description} imageUrl={preview ?? savedImage} />
        </aside>
        </div>

        <div className="cmpFooter">
          <button type="button" className="ecoBtn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className="ecoBtn ecoBtn--primary" disabled={busy}>
            {busy ? "Salvando..." : "Salvar anúncio"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdFormModal;
